import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import { Inventario } from './models/reposicion.model';

@Injectable({
  providedIn: 'root'
})
export class ReposicionService {
  /* ===============================
        STORAGE KEYS
  =============================== */

  private INVENTARIO_KEY = 'reposicion_inventario';
  private SUCURSALES_KEY = 'reposicion_sucursales';

  /* ===============================
        INVENTARIO
  =============================== */

  private inventarioSubject = new BehaviorSubject<Inventario[]>(
    this.cargarInventario()
  );

  readonly inventario$ = this.inventarioSubject.asObservable();

  get inventario(): Inventario[] {
    return this.inventarioSubject.value;
  }

  setInventario(data: Inventario[]) {
    const limpio = data.filter((i) => i.stock >= 0);

    this.inventarioSubject.next(limpio);

    localStorage.setItem(this.INVENTARIO_KEY, JSON.stringify(limpio));
  }

  /* ===============================
        ACTUALIZAR CANTIDAD
  =============================== */

  actualizarCantidad(
    productId: string,
    sucursalCodigo: string,
    cantidad: number
  ) {
    const inventario = [...this.inventario];

    const index = inventario.findIndex(
      (i) => i.productId === productId && i.sucursalCodigo === sucursalCodigo
    );

    if (index !== -1) {
      inventario[index] = {
        ...inventario[index],
        cantidad
      };
    }

    this.setInventario(inventario);
  }

  /* ===============================
        CARGAR INVENTARIO
  =============================== */

  private cargarInventario(): Inventario[] {
    const data = localStorage.getItem(this.INVENTARIO_KEY);

    if (!data) return [];

    try {
      const parsed = JSON.parse(data);

      if (!Array.isArray(parsed)) return [];

      return parsed.filter(
        (i): i is Inventario =>
          i.productId &&
          i.medicamentoCodigo &&
          i.sucursalCodigo &&
          typeof i.stock === 'number' &&
          typeof i.cantidad === 'number'
      );
    } catch {
      return [];
    }
  }

  limpiarInventario() {
    localStorage.removeItem(this.INVENTARIO_KEY);

    this.inventarioSubject.next([]);
  }

  /* ===============================
        SUCURSALES SELECCIONADAS
  =============================== */

  private sucursalesSeleccionadasSubject = new BehaviorSubject<string[]>(
    this.cargarSucursales()
  );

  readonly sucursalesSeleccionadas$ =
    this.sucursalesSeleccionadasSubject.asObservable();

  get sucursalesSeleccionadas(): string[] {
    return this.sucursalesSeleccionadasSubject.value;
  }

  setSucursalesSeleccionadas(sucursales: string[]) {
    this.sucursalesSeleccionadasSubject.next(sucursales);

    localStorage.setItem(this.SUCURSALES_KEY, JSON.stringify(sucursales));
  }

  private cargarSucursales(): string[] {
    const data = localStorage.getItem(this.SUCURSALES_KEY);

    if (!data) return [];

    try {
      const parsed = JSON.parse(data);

      if (!Array.isArray(parsed)) return [];

      return parsed;
    } catch {
      return [];
    }
  }

  limpiarSucursales() {
    localStorage.removeItem(this.SUCURSALES_KEY);

    this.sucursalesSeleccionadasSubject.next([]);
  }

  /* ===============================
        APLICAR SUGERENCIAS
  =============================== */

  private aplicarSugerenciasSubject = new Subject<void>();

  readonly aplicarSugerencias$ = this.aplicarSugerenciasSubject.asObservable();

  aplicarSugerenciasATodos() {
    this.aplicarSugerenciasSubject.next();
  }

  /* ===============================
        VALIDAR STOCK
  =============================== */

  validarStock(sucursalesSeleccionadas: string[]): boolean {
    const mapa = new Map<string, { total: number; stock: number }>();

    for (const r of this.inventario) {
      if (
        !sucursalesSeleccionadas.includes(r.sucursalCodigo) ||
        r.cantidad <= 0 ||
        r.stock <= 0
      )
        continue;

      const actual = mapa.get(r.productId);

      if (!actual) {
        mapa.set(r.productId, {
          total: r.cantidad,
          stock: r.stock
        });
      } else {
        actual.total += r.cantidad;

        if (actual.total > actual.stock) {
          return true;
        }
      }
    }

    return false;
  }

  /* ===============================
        BUSCADOR
  =============================== */

  private filtroBusquedaSubject = new BehaviorSubject<string>('');

  readonly filtroBusqueda$ = this.filtroBusquedaSubject.asObservable();

  setFiltroBusqueda(valor: string) {
    this.filtroBusquedaSubject.next(valor);
  }

  /* ===============================
        FILTRO TIPO
  =============================== */

  private filtroTipoSubject = new BehaviorSubject<string>('todos');

  readonly filtroTipo$ = this.filtroTipoSubject.asObservable();

  setFiltroTipo(valor: string) {
    this.filtroTipoSubject.next(valor);
  }

  private refreshTablaSubject = new Subject<void>();
  refreshTabla$ = this.refreshTablaSubject.asObservable();

  refreshTabla() {
    this.refreshTablaSubject.next();
  }
}
